// SauvegardeTab — Backup & Migration centralized panel for APLEO.
// Groups all the per-collection JSON export/import endpoints AND offers a
// global ZIP snapshot of every editorial collection in one click.
import { useEffect, useState } from "react";
import api from "@/lib/api";

const COLLECTIONS = [
  { key: "intervenants", labelFr: "Passeurs",         labelEn: "Bearers",     endpoint: "/admin/intervenants" },
  { key: "events",       labelFr: "Événements",       labelEn: "Events",      endpoint: "/admin/events" },
  { key: "articles",     labelFr: "Articles",         labelEn: "Articles",    endpoint: null }, // export/import not yet exposed individually
  { key: "photos",       labelFr: "Photos",           labelEn: "Photos",      endpoint: null },
  { key: "partners",     labelFr: "Partenaires",      labelEn: "Partners",    endpoint: null },
  { key: "milestones",   labelFr: "Jalons projet",    labelEn: "Milestones",  endpoint: null },
  { key: "documents",    labelFr: "Documents",        labelEn: "Documents",   endpoint: null },
];

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function CollectionRow({ row, isFr, count }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const exportOne = async () => {
    if (!row.endpoint) return;
    setError("");
    try {
      const res = await api.get(`${row.endpoint}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const ts = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `${row.key}-${ts}.json`);
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Export error");
    }
  };

  const importOne = async (file) => {
    if (!row.endpoint) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("by", "id");
      fd.append("wipe", "false");
      const { data } = await api.post(`${row.endpoint}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Import error");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center py-4 border-b" style={{ borderColor: "var(--border)" }}>
      <div className="sm:col-span-4">
        <div className="font-display text-lg">{isFr ? row.labelFr : row.labelEn}</div>
        <div className="font-body text-xs" style={{ color: "var(--muted)" }}>
          {count != null ? `${count} ${isFr ? "élément(s)" : "items"}` : (isFr ? "Inclus dans le snapshot ZIP" : "Included in ZIP snapshot")}
        </div>
      </div>
      <div className="sm:col-span-5 flex gap-2 flex-wrap">
        {row.endpoint ? (
          <>
            <button type="button"
                    data-testid={`backup-export-${row.key}`}
                    onClick={exportOne}
                    className="font-caps text-[0.6rem] px-2.5 py-2 border"
                    style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.2em" }}>
              ⇩ JSON
            </button>
            <label className="font-caps text-[0.6rem] px-2.5 py-2 border cursor-pointer"
                   style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.2em" }}>
              ⇧ JSON
              <input data-testid={`backup-import-${row.key}`} type="file" accept="application/json,.json"
                     className="hidden" disabled={busy}
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       if (f) importOne(f);
                       e.target.value = "";
                     }} />
            </label>
          </>
        ) : (
          <span className="font-body text-xs italic" style={{ color: "var(--muted)" }}>
            {isFr ? "via le snapshot ZIP global" : "via global ZIP snapshot"}
          </span>
        )}
      </div>
      <div className="sm:col-span-3 text-xs font-body" style={{ color: "var(--muted)" }}>
        {busy && (isFr ? "Import…" : "Importing…")}
        {error && <span style={{ color: "var(--cinnabar)" }}>{error}</span>}
        {result && (
          <span style={{ color: "var(--gold)" }}>
            +{result.inserted} ~{result.updated} <i>({result.total})</i>
          </span>
        )}
      </div>
    </div>
  );
}

export default function SauvegardeTab({ locale }) {
  const isFr = locale === "fr";
  const [counts, setCounts] = useState({});
  const [zipBusy, setZipBusy] = useState(false);
  const [zipError, setZipError] = useState("");
  const [zipResult, setZipResult] = useState(null);

  // ---- Scheduled backups state ----
  const [settings, setSettings] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState("");
  const [archives, setArchives] = useState([]);
  const [runNowBusy, setRunNowBusy] = useState(false);
  const [runNowError, setRunNowError] = useState("");

  const loadSettings = () => api.get("/admin/snapshot/settings").then((r) => setSettings(r.data));
  const loadArchives = () => api.get("/admin/snapshot/archives").then((r) => setArchives(r.data.items || []));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ints = await api.get("/admin/intervenants").catch(() => ({ data: [] }));
        const evts = await api.get("/admin/events").catch(() => ({ data: [] }));
        if (!alive) return;
        setCounts({ intervenants: (ints.data || []).length, events: (evts.data || []).length });
      } catch (_e) { /* silent */ }
    })();
    loadSettings();
    loadArchives();
    return () => { alive = false; };
  }, []);

  const saveSettings = async () => {
    setSettingsBusy(true); setSettingsMsg("");
    try {
      const { data } = await api.put("/admin/snapshot/settings", {
        enabled: !!settings.enabled,
        frequency: settings.frequency || "weekly",
        recipient_email: settings.recipient_email || "",
      });
      setSettings(data);
      setSettingsMsg(isFr ? "Programmation enregistrée." : "Schedule saved.");
    } catch (ex) {
      setSettingsMsg(ex?.response?.data?.detail || "Erreur");
    } finally {
      setSettingsBusy(false);
      setTimeout(() => setSettingsMsg(""), 4000);
    }
  };

  const runNow = async () => {
    setRunNowBusy(true); setRunNowError("");
    try {
      await api.post("/admin/snapshot/run-now");
      await loadArchives();
      await loadSettings();
    } catch (ex) {
      setRunNowError(ex?.response?.data?.detail || "Erreur");
    } finally { setRunNowBusy(false); }
  };

  // -------- Global ZIP snapshot --------
  const exportZip = async () => {
    setZipError(""); setZipBusy(true);
    try {
      const res = await api.get("/admin/snapshot/export", { responseType: "blob" });
      const ts = new Date().toISOString().slice(0, 10);
      downloadBlob(res.data, `prieure-snapshot-${ts}.zip`);
    } catch (ex) {
      setZipError(ex?.response?.data?.detail || "ZIP export error");
    } finally { setZipBusy(false); }
  };

  const importZip = async (file) => {
    setZipError(""); setZipResult(null); setZipBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("wipe", "false");
      const { data } = await api.post("/admin/snapshot/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setZipResult(data);
    } catch (ex) {
      setZipError(ex?.response?.data?.detail || "ZIP import error");
    } finally { setZipBusy(false); }
  };

  return (
    <div data-testid="admin-sauvegarde-tab" className="space-y-8">
      {/* Header */}
      <div>
        <div className="font-caps text-[0.7rem]" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>
          APLEO · {isFr ? "Sauvegarde & Migration" : "Backup & Migration"}
        </div>
        <h2 className="font-display text-3xl mt-2">
          {isFr ? "Sauvegarde & Migration" : "Backup & Migration"}
        </h2>
        <p className="font-body text-sm mt-3 max-w-3xl" style={{ color: "var(--muted)" }}>
          {isFr
            ? "Téléchargez un instantané complet du site (Passeurs, Événements, Articles, Photos, Partenaires, Jalons, Documents…) en un seul ZIP, ou exportez/importez chaque collection individuellement en JSON. L'import est non destructif (upsert par `id`) — vous pouvez le réexécuter sans risque pour synchroniser preview → production."
            : "Download a full site snapshot (Bearers, Events, Articles, Photos, Partners, Milestones, Documents…) as a single ZIP, or export/import each collection individually as JSON. Imports are non-destructive (upsert by `id`) — safe to re-run for preview → production sync."}
        </p>
      </div>

      {/* ---- Global snapshot ZIP card ---- */}
      <div className="border p-6 sm:p-8 bg-white" style={{ borderColor: "var(--border)" }} data-testid="backup-snapshot-zip">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-caps text-[0.65rem]" style={{ color: "var(--cinnabar)", letterSpacing: "0.25em" }}>
              {isFr ? "SNAPSHOT COMPLET (ZIP)" : "FULL SNAPSHOT (ZIP)"}
            </div>
            <h3 className="font-display text-2xl mt-1">
              {isFr ? "Sauvegarde mensuelle du site" : "Monthly site backup"}
            </h3>
            <p className="font-body text-sm mt-2" style={{ color: "var(--muted)" }}>
              {isFr
                ? "Contient toutes les collections éditoriales en un seul fichier `prieure-snapshot-YYYY-MM-DD.zip`. Idéal pour archive trimestrielle, migration preview → production ou restauration."
                : "Contains every editorial collection in a single `prieure-snapshot-YYYY-MM-DD.zip`. Ideal for quarterly archive, preview → production migration, or restore."}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch">
            <button type="button"
                    data-testid="backup-snapshot-export-btn"
                    onClick={exportZip}
                    disabled={zipBusy}
                    className="font-caps text-[0.7rem] px-4 py-3 border-2"
                    style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.22em" }}>
              ⇩ {isFr ? "TÉLÉCHARGER LE ZIP" : "DOWNLOAD ZIP"}
            </button>
            <label className="font-caps text-[0.7rem] px-4 py-3 border-2 cursor-pointer text-center"
                   style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.22em" }}>
              ⇧ {isFr ? "IMPORTER LE ZIP" : "IMPORT ZIP"}
              <input data-testid="backup-snapshot-import-file" type="file" accept=".zip,application/zip"
                     className="hidden" disabled={zipBusy}
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       if (f) importZip(f);
                       e.target.value = "";
                     }} />
            </label>
          </div>
        </div>
        {zipBusy && (
          <p className="font-body italic text-sm mt-4" style={{ color: "var(--muted)" }}>
            {isFr ? "Opération en cours…" : "Working…"}
          </p>
        )}
        {zipError && (
          <p className="font-body text-sm mt-4" style={{ color: "var(--cinnabar)" }} data-testid="backup-snapshot-error">
            {zipError}
          </p>
        )}
        {zipResult && (
          <div className="mt-5 p-4 border-l-2"
               style={{ borderColor: "var(--gold)", background: "var(--parchment-2)" }}
               data-testid="backup-snapshot-result">
            <div className="font-caps text-[0.62rem]" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
              {isFr ? "IMPORT TERMINÉ" : "IMPORT DONE"}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-3 font-body text-sm">
              {Object.entries(zipResult.collections || {}).map(([coll, s]) => (
                <div key={coll} data-testid={`backup-zip-stat-${coll}`}>
                  <div className="font-caps text-[0.6rem]" style={{ color: "var(--ink)", letterSpacing: "0.18em" }}>
                    {coll}
                  </div>
                  <div style={{ color: "var(--muted)" }}>
                    +{s.inserted} ~{s.updated} <i>({s.total})</i>
                  </div>
                </div>
              ))}
            </div>
            {Array.isArray(zipResult.skipped_files) && zipResult.skipped_files.length > 0 && (
              <ul className="mt-3 list-disc pl-5" style={{ color: "var(--cinnabar)" }}>
                {zipResult.skipped_files.map((sf, i) => (
                  <li key={i} className="font-body text-xs">
                    <strong>{sf.name}</strong> — {sf.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ---- Per-collection export/import ---- */}
      <div className="border bg-white" style={{ borderColor: "var(--border)" }}>
        <div className="p-6 sm:p-8 border-b" style={{ borderColor: "var(--border)" }}>
          <h3 className="font-display text-xl">
            {isFr ? "Export / Import par collection" : "Export / Import per collection"}
          </h3>
          <p className="font-body text-sm mt-2" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Granularité fine : sauvegarde ou réinjection d'une seule collection à la fois (JSON)."
              : "Fine-grained: backup or re-inject a single collection at a time (JSON)."}
          </p>
        </div>
        <div className="px-6 sm:px-8">
          {COLLECTIONS.map((row) => (
            <CollectionRow key={row.key} row={row} isFr={isFr}
                           count={counts[row.key]} />
          ))}
        </div>
      </div>

      {/* ---- Scheduled backups ---- */}
      <div className="border bg-white p-6 sm:p-8" style={{ borderColor: "var(--border)" }} data-testid="backup-schedule">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
          <div>
            <div className="font-caps text-[0.65rem]" style={{ color: "var(--lapis)", letterSpacing: "0.25em" }}>
              {isFr ? "PROGRAMMATION AUTOMATIQUE" : "AUTOMATIC SCHEDULE"}
            </div>
            <h3 className="font-display text-2xl mt-1">
              {isFr ? "Sauvegarde récurrente" : "Recurring backup"}
            </h3>
          </div>
          <button type="button"
                  data-testid="backup-run-now-btn"
                  onClick={runNow}
                  disabled={runNowBusy}
                  className="font-caps text-[0.65rem] px-3 py-2 border"
                  style={{ borderColor: "var(--gold)", color: "var(--gold)", letterSpacing: "0.22em" }}>
            ▶ {isFr ? "LANCER MAINTENANT" : "RUN NOW"}
          </button>
        </div>
        <p className="font-body text-sm mb-4" style={{ color: "var(--muted)" }}>
          {isFr
            ? "Génère automatiquement un snapshot ZIP, le dépose dans l'Object Storage Emergent (URL pérenne) et envoie un e-mail Brevo avec le lien de téléchargement."
            : "Automatically builds a snapshot ZIP, stores it in Emergent Object Storage (permanent URL) and emails the download link via Brevo."}
        </p>
        {runNowError && (
          <p className="font-body text-sm mb-2" style={{ color: "var(--cinnabar)" }} data-testid="backup-run-now-error">
            {runNowError}
          </p>
        )}
        {settings && (
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-end">
            <label className="sm:col-span-3 block">
              <span className="label-caps">{isFr ? "ACTIVÉE" : "ENABLED"}</span>
              <select data-testid="backup-enabled"
                      className="input-classic mt-1 w-full"
                      value={settings.enabled ? "yes" : "no"}
                      onChange={(e) => setSettings({ ...settings, enabled: e.target.value === "yes" })}>
                <option value="no">{isFr ? "Désactivée" : "Disabled"}</option>
                <option value="yes">{isFr ? "Activée" : "Enabled"}</option>
              </select>
            </label>
            <label className="sm:col-span-3 block">
              <span className="label-caps">{isFr ? "FRÉQUENCE" : "FREQUENCY"}</span>
              <select data-testid="backup-frequency"
                      className="input-classic mt-1 w-full"
                      value={settings.frequency || "weekly"}
                      onChange={(e) => setSettings({ ...settings, frequency: e.target.value })}>
                <option value="daily">{isFr ? "Quotidienne" : "Daily"}</option>
                <option value="weekly">{isFr ? "Hebdomadaire" : "Weekly"}</option>
                <option value="monthly">{isFr ? "Mensuelle" : "Monthly"}</option>
              </select>
            </label>
            <label className="sm:col-span-4 block">
              <span className="label-caps">{isFr ? "E-MAIL DESTINATAIRE" : "RECIPIENT E-MAIL"}</span>
              <input data-testid="backup-recipient"
                     type="email"
                     placeholder="contact@prieure-saint-leonard.fr"
                     className="input-classic mt-1 w-full"
                     value={settings.recipient_email || ""}
                     onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })} />
            </label>
            <div className="sm:col-span-2 flex">
              <button type="button"
                      data-testid="backup-save-settings"
                      onClick={saveSettings}
                      disabled={settingsBusy}
                      className="btn btn-primary w-full">
                {isFr ? "Enregistrer" : "Save"}
              </button>
            </div>
          </div>
        )}
        {settings && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 font-body text-xs"
               style={{ color: "var(--muted)" }}>
            <div>
              <span className="font-caps" style={{ letterSpacing: "0.2em" }}>
                {isFr ? "DERNIÈRE EXÉCUTION" : "LAST RUN"} :
              </span>{" "}
              {settings.last_run_at
                ? new Date(settings.last_run_at).toLocaleString(isFr ? "fr-FR" : "en-GB")
                : (isFr ? "jamais" : "never")}
            </div>
            <div>
              <span className="font-caps" style={{ letterSpacing: "0.2em" }}>
                {isFr ? "PROCHAINE EXÉCUTION" : "NEXT RUN"} :
              </span>{" "}
              {settings.enabled && settings.next_run_at
                ? new Date(settings.next_run_at).toLocaleString(isFr ? "fr-FR" : "en-GB")
                : (isFr ? "—" : "—")}
            </div>
          </div>
        )}
        {settingsMsg && (
          <p className="font-body text-sm mt-3" style={{ color: "var(--gold)" }} data-testid="backup-settings-msg">
            {settingsMsg}
          </p>
        )}
      </div>

      {/* ---- Archive history ---- */}
      <div className="border bg-white" style={{ borderColor: "var(--border)" }} data-testid="backup-archives">
        <div className="p-6 sm:p-8 border-b" style={{ borderColor: "var(--border)" }}>
          <h3 className="font-display text-xl">
            {isFr ? "Historique des sauvegardes" : "Backup history"}
          </h3>
          <p className="font-body text-sm mt-2" style={{ color: "var(--muted)" }}>
            {isFr
              ? `Les ${archives.length} dernier(s) snapshot(s) automatique(s) ou manuel(s). Limité aux 50 plus récents.`
              : `The ${archives.length} most recent automatic or manual snapshot(s). Capped at 50.`}
          </p>
        </div>
        {archives.length === 0 ? (
          <div className="p-6 sm:p-8 font-body italic text-sm" style={{ color: "var(--muted)" }}>
            {isFr ? "Aucune sauvegarde enregistrée pour le moment." : "No backup recorded yet."}
          </div>
        ) : (
          <div className="px-6 sm:px-8">
            {archives.map((a) => (
              <div key={a.id}
                   className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center py-3 border-b"
                   style={{ borderColor: "var(--border)" }}
                   data-testid={`backup-archive-${a.id}`}>
                <div className="sm:col-span-4 font-body text-sm">
                  {new Date(a.created_at).toLocaleString(isFr ? "fr-FR" : "en-GB")}
                </div>
                <div className="sm:col-span-2 font-body text-xs" style={{ color: "var(--muted)" }}>
                  {((a.size_bytes || 0) / 1024).toFixed(1)} Ko
                </div>
                <div className="sm:col-span-3 font-body text-xs" style={{ color: a.email_sent ? "var(--gold)" : "var(--muted)" }}>
                  {a.email_sent
                    ? (isFr ? "✓ E-mail envoyé" : "✓ Email sent")
                    : (isFr ? "○ Pas d'e-mail" : "○ No email")}
                </div>
                <div className="sm:col-span-3 flex justify-end">
                  {a.url && (
                    <a href={a.url} target="_blank" rel="noreferrer noopener"
                       className="font-caps text-[0.62rem] px-3 py-2 border"
                       style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.2em" }}
                       data-testid={`backup-archive-download-${a.id}`}>
                      ⇩ {isFr ? "TÉLÉCHARGER" : "DOWNLOAD"}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Safety note */}
      <div className="border-l-4 p-4 font-body text-sm"
           style={{ borderColor: "var(--gold)", background: "var(--parchment)" }}>
        <strong>{isFr ? "Note de sécurité :" : "Safety note:"}</strong>{" "}
        {isFr
          ? "Les imports sont des `upsert` par `id`. Les fiches qui ne figurent PAS dans le fichier importé sont préservées intactes en base — aucune suppression silencieuse. Les collections sensibles (utilisateurs, dons, paiements, contacts, inscriptions) ne sont JAMAIS exportées ni écrasées par cet outil."
          : "Imports are upserts by `id`. Records that are NOT in the imported file remain untouched in the database — no silent deletions. Sensitive collections (users, donations, payments, contacts, registrations) are NEVER exported or overwritten by this tool."}
      </div>
    </div>
  );
}
